import { List, Map, fromJS } from 'immutable'
import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import { Storage } from '@aytm/lib/storage'

import {
  isSkipperGuidedDraftReport,
  isSkipperGuidedReport
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'
import { generationReportId } from '@aytm/app/stats/stores/guidedReportGenerationPoll'

const ERROR = 'we are having a hard time completing your request.'

const STATIC_REPORTS_IDS = ['skipper', 'concept']
const VIRTUAL_REPORTS_IDS = ['term-report']

// Reports are addressed by id everywhere. The panel state keys items by id, so its
// path is derivable; the model keeps items in an ordered List, so the index is looked
// up on demand against the list being mutated rather than threaded down as a prop.
export const reportStatePath = (id, ...rest) => ['items', id, ...rest]

const reportModelPath = (listModel, id) => ['items', listModel.get('items').findIndex(item => item.get('id') == id)]

export const updateListState = ({ updates, listState, actions } = {}) => {
  const newListState = listState.withMutations(map => {
    updates.forEach(x => {
      null !== x.value ? map.setIn([...x.path], x.value) : map.deleteIn([...x.path])
    })
  })
  actions.updateState(newListState)

  return newListState
}

const wrappedPostFetch = (url, data = {}) =>
  Storage.Encode(data)
    .then(_bsfz =>
      railsPostFetch(url, {
        body: JSON.stringify({
          _bsfz
        })
      })
    )
    .then(response => response.json())

const getModelData = model => {
  const params = model.get('params')
  const config = model.get('config')
  const virtual = model.get('virtual')
  const schema = model.get('schema')
  const storytellings = model.get('storytellings')
  const storytellingNotes = model.get('storytellingNotes')
  const logicData = model.get('logicData')

  const _virtual = Object.entries(null !== virtual ? virtual.toJS() : {}).reduce((acc, [key, value]) => {
    delete value.preset
    acc[key] = value
    return acc
  }, {})

  const data = {
    reportId: model.get('id'),
    params: params.toJS(),
    config: config != undefined && null !== config ? config.toJS() : null,
    virtual: _virtual != undefined && null !== _virtual ? _virtual : null,
    schema: schema != undefined && null !== schema ? schema.toJS() : null,
    storytellings: storytellings != undefined && null !== storytellings ? storytellings.toJS() : {},
    storytellingNotes: storytellingNotes != undefined && null !== storytellingNotes ? storytellingNotes.toJS() : {},
    logicData: logicData != undefined && null !== logicData ? logicData.toJS() : null
  }

  const scripts = model.get('scripts')
  scripts && 0 < scripts.size && (data.scripts = scripts.toJS())

  return data
}

const setModelData = (state, listModel, modelPath) => {
  const initials = state.get('initials')
  return listModel.withMutations(map => {
    if (initials != undefined) {
      map
        .setIn([...modelPath, 'params'], fromJS(initials.get('params')))
        .setIn([...modelPath, 'config'], fromJS(initials.get('config')))
        .setIn([...modelPath, 'virtual'], fromJS(initials.get('virtual')))
        .setIn([...modelPath, 'schema'], fromJS(initials.get('schema')))
        .setIn([...modelPath, 'logicData'], fromJS(initials.get('logicData')))

      const scripts = fromJS(initials.get('scripts') || {})
      scripts && 0 < scripts.size && map.setIn([...modelPath, 'scripts'], scripts)
    }
  })
}

const decodeModelData = listModel => {
  const skipperReports = listModel
    .get('items')
    .filter((r, index) => 0 != index && ['skipper'].includes(r.get('type')) && !isSkipperGuidedReport(r))
  const conceptReports = listModel.get('items').filter((r, index) => 0 != index && ['concept'].includes(r.get('type')))
  // index 0 is the pinned Default slot and is kept by the base filter below — a guided report
  // promoted into it must not also be re-inserted into the guided group, or it renders twice
  const skipperGuidedReports = listModel.get('items').filter((r, index) => 0 != index && isSkipperGuidedReport(r))
  return listModel.withMutations(map => {
    map.update('items', List(), items =>
      items.filter((r, index) => 0 == index || ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.get('type')))
    )
    for (const [index, staticReport] of skipperReports.entries()) {
      map.update('items', List(), items => items.insert(index + 1, Map(staticReport)))
    }
    for (const [index, staticReport] of conceptReports.entries()) {
      map.update('items', List(), items => items.insert(index + skipperReports.size + 1, Map(staticReport)))
    }
    for (const [index, skipperGuidedReport] of skipperGuidedReports.entries()) {
      map.update('items', List(), items =>
        items.insert(index + skipperReports.size + conceptReports.size + 1, Map(skipperGuidedReport))
      )
    }
  })
}
const encodeModelData = (listModel, newListModel) => {
  const skipperReports = newListModel
    .get('items')
    .filter((r, index) => 0 != index && ['skipper'].includes(r.get('type')) && !isSkipperGuidedReport(r))
  const conceptReports = newListModel
    .get('items')
    .filter((r, index) => 0 != index && ['concept'].includes(r.get('type')))
  // virtual entries are dropped by the decode, so they come from the pre-decode list; guided ones
  // survive it and must be read back in the order it holds, or a reorder among them is un-drawn
  const virtualReports = listModel.get('items').filter(r => VIRTUAL_REPORTS_IDS.includes(r.get('type')))
  const skipperGuidedReports = newListModel.get('items').filter((r, index) => 0 != index && isSkipperGuidedReport(r))
  return newListModel.withMutations(map => {
    map.update('items', List(), items =>
      items.filter((r, index) => 0 == index || ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.get('type')))
    )
    for (const [index, staticReport] of skipperReports.entries()) {
      map.update('items', List(), items => items.insert(index + 1, Map(staticReport)))
    }
    for (const [index, staticReport] of conceptReports.entries()) {
      map.update('items', List(), items => items.insert(index + skipperReports.size + 1, Map(staticReport)))
    }
    for (const [index, virtualReport] of virtualReports.entries()) {
      map.update('items', List(), items =>
        items.insert(index + skipperReports.size + conceptReports.size + 1, Map(virtualReport))
      )
    }
    for (const [index, skipperGuidedReport] of skipperGuidedReports.entries()) {
      map.update('items', List(), items =>
        items.insert(
          index + skipperReports.size + conceptReports.size + virtualReports.size + 1,
          Map(skipperGuidedReport)
        )
      )
    }
  })
}

// A reorder that fails was never persisted, so the optimistic order on screen no longer matches
// the server's. Put the pre-drop list back rather than leave the two disagreeing until a reload.
const restoreListOrder = (listModel, actions) => actions.updateModel(listModel, { type: 'reorder' })

// Re-draw the list from the ordering the reorder answered with, which is authoritative for every
// report it names. A client-only draft has no report id, so it is in neither the request nor that
// ordering — hold it aside and put it back after the row it followed, or the ordering's length
// cuts it off the list.
const rebuildListOrder = (listModel, data) => {
  const order = data.order.filter((item, pos, self) => self.indexOf(item) == pos)
  const decodedListModel = decodeModelData(listModel)
  const unsentItems = decodedListModel
    .get('items')
    .map((item, index) => ({ item, afterId: 0 < index ? decodedListModel.getIn(['items', index - 1, 'id']) : null }))
    .filter(({ item }) => !(0 < item.get('id')))

  return encodeModelData(
    listModel,
    decodedListModel.withMutations(map => {
      map.update('items', List(), items => items.filter(item => 0 < item.get('id')))

      order.forEach((id, index) => {
        const itemIndex = map.get('items').findIndex(item => item.get('id') == id)
        if (0 > itemIndex) {
          const newListItem = fromJS(data.reports.find(x => x.id == id))
          map.update('items', List(), items => items.insert(index, newListItem))
        } else if (itemIndex != index) {
          const listItem = fromJS(map.getIn(['items', itemIndex]))
          map.deleteIn(['items', itemIndex]).update('items', List(), items => items.insert(index, listItem))
        }
      })

      map.update('items', List(), items =>
        unsentItems.reduce((list, { item, afterId }) => {
          const at = list.findIndex(r => r.get('id') == afterId) + 1
          return list.insert(0 < at ? at : list.size, item)
        }, items.setSize(order.length))
      )
    })
  )
}

export const dropListItemDefault = (props = {}) => {
  const { url, listState, listModel, model, actions } = props

  updateListState({
    updates: [{ path: ['reorder'], value: null }],
    listState,
    actions
  })

  if (null === model) return

  let newListModel

  const oldListIndex = listModel.get('items').findIndex(item => item.get('id') == model.id)
  if (0 == oldListIndex) return

  const defaultListItem = listModel.get('items').get(0)

  if (STATIC_REPORTS_IDS.includes(defaultListItem.get('type'))) {
    newListModel = listModel.withMutations(map => {
      map
        .deleteIn(['items', oldListIndex])
        .deleteIn(['items', 0])
        .update('items', List(), items => items.insert(0, fromJS(model)).insert(1, fromJS(defaultListItem)))
    })
  } else {
    const staticReports = listModel
      .get('items')
      .filter(r => r.get('id') != model.id && STATIC_REPORTS_IDS.includes(r.get('type')) && !isSkipperGuidedReport(r))
    const virtualReports = listModel.get('items').filter(r => VIRTUAL_REPORTS_IDS.includes(r.get('type')))
    // exclude the promoted report, as staticReports does — counting it here would push the
    // demoted default one slot too far when the report being promoted is itself guided
    const skipperGuidedReports = listModel.get('items').filter(r => r.get('id') != model.id && isSkipperGuidedReport(r))

    newListModel = listModel.withMutations(map => {
      map
        .deleteIn(['items', oldListIndex])
        .deleteIn(['items', 0])
        .update('items', List(), items =>
          items
            .insert(0, fromJS(model))
            .insert(staticReports.size + virtualReports.size + skipperGuidedReports.size + 1, fromJS(defaultListItem))
        )
    })
  }
  actions.updateModel(newListModel, { type: 'reorder', ids: [] })

  // Client-only draft placeholders carry a negative id and were never persisted —
  // exclude them from the reorder payload so only real report ids reach the server.
  const order = decodeModelData(newListModel)
    .get('items')
    .map(item => item.get('id'))
    .filter(id => 0 < id)
    .toJS()

  wrappedPostFetch(url, {
    do: 'reorder',
    order
  })
    .then(data => {
      if (data.order) {
        actions.updateModel(rebuildListOrder(listModel, data), { type: 'reorder' })
      }
      if (!data.status) {
        if (!data.order) restoreListOrder(listModel, actions)
        actions.error(data.error || ERROR)
      }
    })
    .catch(() => {
      restoreListOrder(listModel, actions)
      actions.error(ERROR)
    })
}

export const dropListItemAutomated = (props = {}) => {
  const { url, listState, listModel, model, actions } = props

  updateListState({
    updates: [{ path: ['reorder'], value: null }],
    listState,
    actions
  })

  if (null === model) return

  const oldListIndex = listModel.get('items').findIndex(item => item.get('id') == model.id)
  if (0 != oldListIndex) return

  const defaultListIndex = listModel
    .get('items')
    .findIndex(r => ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.get('type')))
  const defaultListItem = listModel.get('items').get(defaultListIndex)

  const skipperReports = listModel
    .get('items')
    .filter((r, index) => 0 != index && ['skipper'].includes(r.get('type')) && !isSkipperGuidedReport(r))
  const conceptReports = listModel.get('items').filter((r, index) => 0 != index && ['concept'].includes(r.get('type')))

  const newListModel = listModel.withMutations(map => {
    map
      .deleteIn(['items', oldListIndex])
      .deleteIn(['items', defaultListIndex - 1])
      .update('items', List(), items =>
        items
          .insert(0, fromJS(defaultListItem))
          .insert(
            'skipper' == model.type
              ? 1
              : 'concept' == model.type
                ? skipperReports.size + 1
                : skipperReports.size + conceptReports.size + 1,
            fromJS(model)
          )
      )
  })
  actions.updateModel(newListModel, { type: 'reorder', ids: [] })

  const newListIndex = newListModel.get('items').findIndex(item => item.get('id') == model.id)
  if (newListIndex == oldListIndex) return

  // Client-only draft placeholders carry a negative id and were never persisted —
  // exclude them from the reorder payload so only real report ids reach the server.
  const order = decodeModelData(newListModel)
    .get('items')
    .map(item => item.get('id'))
    .filter(id => 0 < id)
    .toJS()

  wrappedPostFetch(url, {
    do: 'reorder',
    order
  })
    .then(data => {
      if (data.order) {
        actions.updateModel(rebuildListOrder(listModel, data), { type: 'reorder' })
      }
      if (!data.status) {
        if (!data.order) restoreListOrder(listModel, actions)
        actions.error(data.error || ERROR)
      }
    })
    .catch(() => {
      restoreListOrder(listModel, actions)
      actions.error(ERROR)
    })
}

export const dropListItemGuided = (props = {}) => {
  const { url, listIndex, listState, listModel, model, actions } = props

  updateListState({
    updates: [{ path: ['reorder'], value: null }],
    listState,
    actions
  })

  if (null === model) return

  const oldListIndex = listModel.get('items').findIndex(item => item.get('id') == model.id)
  // slot 0 is the pinned Default report, not part of the guided group
  if (0 == oldListIndex) return

  // the guided group is rendered after the static (skipper/concept) and virtual groups
  const staticReports = listModel
    .get('items')
    .filter((r, index) => 0 != index && STATIC_REPORTS_IDS.includes(r.get('type')) && !isSkipperGuidedReport(r))
  const virtualReports = listModel.get('items').filter(r => VIRTUAL_REPORTS_IDS.includes(r.get('type')))

  const newListModel = listModel.withMutations(map => {
    map
      .deleteIn(['items', oldListIndex])
      .update('items', List(), items =>
        items.insert(listIndex + staticReports.size + virtualReports.size + 1, fromJS(model))
      )
  })

  actions.updateModel(newListModel, { type: 'reorder', ids: [] })

  const newListIndex = newListModel.get('items').findIndex(item => item.get('id') == model.id)
  if (newListIndex == oldListIndex) return

  const order = decodeModelData(newListModel)
    .get('items')
    .map(item => item.get('id'))
    .filter(id => 0 < id)
    .toJS()

  wrappedPostFetch(url, {
    do: 'reorder',
    order
  })
    .then(data => {
      if (data.order) {
        actions.updateModel(rebuildListOrder(listModel, data), { type: 'reorder' })
      }
      if (!data.status) {
        if (!data.order) restoreListOrder(listModel, actions)
        actions.error(data.error || ERROR)
      }
    })
    .catch(() => {
      restoreListOrder(listModel, actions)
      actions.error(ERROR)
    })
}

export const dropListItemOther = (props = {}) => {
  const { url, listIndex, listState, listModel, model, actions } = props

  updateListState({
    updates: [{ path: ['reorder'], value: null }],
    listState,
    actions
  })

  if (null === model) return

  let newListModel

  const oldListIndex = listModel.get('items').findIndex(item => item.get('id') == model.id)
  if (0 == oldListIndex && 0 == listIndex) return

  const staticReports = listModel
    .get('items')
    .filter((r, index) => 0 != index && STATIC_REPORTS_IDS.includes(r.get('type')) && !isSkipperGuidedReport(r))
  const virtualReports = listModel.get('items').filter(r => VIRTUAL_REPORTS_IDS.includes(r.get('type')))
  const skipperGuidedReports = listModel.get('items').filter((r, index) => 0 != index && isSkipperGuidedReport(r))

  if (0 == oldListIndex) {
    const defaultListIndex = listModel
      .get('items')
      .findIndex(
        r => r.get('id') != model.id && ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.get('type'))
      )
    const defaultListItem = listModel.get('items').get(defaultListIndex)

    newListModel = listModel.withMutations(map => {
      map
        .deleteIn(['items', oldListIndex])
        .deleteIn(['items', defaultListIndex - 1])
        .update('items', List(), items =>
          items
            .insert(0, fromJS(defaultListItem))
            .insert(listIndex + staticReports.size + virtualReports.size + skipperGuidedReports.size + 1, fromJS(model))
        )
    })
  } else {
    newListModel = listModel.withMutations(map => {
      map
        .deleteIn(['items', oldListIndex])
        .update('items', List(), items =>
          items.insert(
            listIndex + staticReports.size + virtualReports.size + skipperGuidedReports.size + 1,
            fromJS(model)
          )
        )
    })
  }
  actions.updateModel(newListModel, { type: 'reorder', ids: [] })

  const newListIndex = newListModel.get('items').findIndex(item => item.get('id') == model.id)
  if (newListIndex == oldListIndex) return

  // Client-only draft placeholders carry a negative id and were never persisted —
  // exclude them from the reorder payload so only real report ids reach the server.
  const order = decodeModelData(newListModel)
    .get('items')
    .map(item => item.get('id'))
    .filter(id => 0 < id)
    .toJS()

  wrappedPostFetch(url, {
    do: 'reorder',
    order
  })
    .then(data => {
      if (data.order) {
        actions.updateModel(rebuildListOrder(listModel, data), { type: 'reorder' })
      }
      if (!data.status) {
        if (!data.order) restoreListOrder(listModel, actions)
        actions.error(data.error || ERROR)
      }
    })
    .catch(() => {
      restoreListOrder(listModel, actions)
      actions.error(ERROR)
    })
}

export const addListItem = (props = {}) => {
  const { url, listState, listModel, state, model, actions } = props

  // "Save as a new report" carries the source report's state, so the new report starts
  // from that report's saved values and the source is reset. A blank report has no
  // source: it passes no state and neither step applies.
  const sourceId = undefined != state ? model.get('id') : null

  updateListState({
    updates:
      null != sourceId
        ? [
            { path: ['add'], value: true },
            { path: reportStatePath(sourceId, 'process'), value: 'add' }
          ]
        : [{ path: ['add'], value: true }],
    listState,
    actions
  })

  const data = getModelData(model)

  wrappedPostFetch(url, {
    do: 'add',
    share: data
  })
    .then(data => {
      if (!Number.isNaN(Number(data.id)) && 0 < Number(data.id)) {
        const staticReports = listModel
          .get('items')
          .filter((r, index) => 0 != index && STATIC_REPORTS_IDS.includes(r.get('type')) && !isSkipperGuidedReport(r))
        const skipperGuidedReports = listModel.get('items').filter((r, index) => 0 != index && isSkipperGuidedReport(r))

        const newListItem = fromJS(data)
        const newListModel = encodeModelData(
          listModel,
          decodeModelData(
            null != sourceId ? setModelData(state, listModel, reportModelPath(listModel, sourceId)) : listModel
          ).update('items', List(), items =>
            items.insert(staticReports.size + skipperGuidedReports.size + 1, newListItem)
          )
        )

        actions.updateModel(newListModel, {
          type: 'add',
          id: newListItem.get('id'),
          siblingId: sourceId
        })
      } else {
        actions.error(data.error || ERROR, sourceId)
      }
    })
    .catch(() => actions.error(ERROR, sourceId))
}

// Client-only guided placeholder skeleton. In the two-phase guided flow the
// persisted StatReport is minted by the generation pipeline (POST
// /api/v1/skipper/report, bearer-authed) once the guided chat completes — there
// is no session-authed "create draft" call to make here. Negative id marks it
// local/unsaved; the absent origin leaves it guided (see isSkipperGuidedDraftReport).
// No fabricated metadata — the structural skeleton only, with the given status and
// guided_report snapshot.
const buildGuidedPlaceholderItem = (status, guidedReport, id = null) =>
  fromJS({
    id: id || -(Math.floor(Math.random() * 8888888) + 1111111),
    title: 'New Guided Report',
    type: 'skipper',
    hidden: false,
    protected: false,
    editable: false,
    original: false,
    params: {
      filter_mode: 'basic',
      chart_mode: 'respondents',
      legend: 'long',
      question_logic: 'show',
      question_aliases: 'show',
      rType: 'cases',
      color_theme: '0',
      cl_export_visibility: 'show',
      c1: true
    },
    virtual: null,
    storytellings: null,
    storytellingNotes: null,
    schema: [],
    scripts: null,
    logicData: [],
    config: null,
    guided_report: guidedReport,
    status
  })

export const addSkipperGuidedListItem = (props = {}) => {
  const { listState, listModel, actions } = props

  updateListState({
    updates: [{ path: ['add'], value: true }],
    listState,
    actions
  })

  const newListItem = buildGuidedPlaceholderItem('draft', {})

  const staticReports = listModel
    .get('items')
    .filter((r, index) => 0 != index && STATIC_REPORTS_IDS.includes(r.get('type')) && !isSkipperGuidedReport(r))
  const virtualReports = listModel.get('items').filter(r => VIRTUAL_REPORTS_IDS.includes(r.get('type')))

  const newListModel = listModel.update('items', List(), items =>
    items.insert(staticReports.size + virtualReports.size + 1, newListItem)
  )

  actions.updateModel(newListModel, {
    type: 'add',
    id: newListItem.get('id')
  })
}

// Flip a row to "generating" and stamp the launch handle onto its guided_report snapshot. When the
// handle carries the real report id the row also adopts it, so a client-only draft stops being
// client-only: it addresses the persisted StatReport, can be opened, and the id-based status poller
// can track it ('reid' re-keys the row in window.surveyReports and the panel state).
const startRowGeneration = (listModel, actions, index, generation) => {
  const row = listModel.getIn(['items', index])
  const prevId = row.get('id')
  const reportId = generationReportId(generation)

  const updatedItem = row
    .set('id', reportId || prevId)
    .set('status', 'generating')
    .set('guided_report', (row.get('guided_report') || Map()).merge(fromJS({ generation })))

  const newListModel = listModel.setIn(['items', index], updatedItem)

  actions.updateModel(
    newListModel,
    reportId && reportId != prevId ? { type: 'reid', id: reportId, prevId } : { type: 'update', id: prevId }
  )
  return true
}

export const markSkipperGuidedDraftGenerating = (props = {}) => {
  const { listModel, actions, generation } = props

  const index = listModel.get('items').findIndex(item => isSkipperGuidedDraftReport(item))
  if (0 > index) return false // draft already gone — reloaded, deleted, or switched away

  return startRowGeneration(listModel, actions, index, generation)
}

// Ensure a row is tracking a (fresh) launch handle: flip an existing draft to generating, no-op on a
// report that has already settled or is already generating, or create a new generating placeholder
// when none exists (recovering an in-flight generation on page load).
export const ensureSkipperGuidedGeneratingDraft = (props = {}) => {
  const { generation, listModel, actions, recovered = false } = props
  const items = listModel.get('items')

  // The handle's report is already in the list, so that row owns this generation and no other row
  // may take it. Resolved BEFORE the draft branch below: a draft flipped on this handle would adopt
  // an id another row already holds, putting two rows on the list under one id. A row that has
  // settled is left exactly as it is — the handle survives its own generation by an hour, so one
  // naming a settled report describes work that has already ended, and its row's own status is the
  // authority. Only a row still saying 'generating' takes the handle, and only from a generation
  // dispatched while this poll was running: an in-flight generation recovered on load needs no
  // flip, since the page bootstrap already published that status.
  const reportId = generationReportId(generation)
  const existing = reportId ? items.findIndex(item => reportId == item.get('id')) : -1
  if (0 <= existing) {
    if ('generating' != items.getIn([existing, 'status'])) return false
    return !recovered && startRowGeneration(listModel, actions, existing, generation)
  }

  // The backend never clears the launch handle, so a handle stays "fresh" for 60 minutes after
  // its report has already landed. Without this guard, reloading after a completed generation
  // would resurrect a phantom generating row next to the finished report — or flip a waiting
  // draft onto a generation that has already ended somewhere else in the list.
  const generationId = generation?.generation_id
  if (
    generationId &&
    0 <= items.findIndex(item => 0 < item.get('id') && generationId == item.getIn(['guided_report', 'generation_id']))
  ) {
    return
  }

  // A draft is still awaiting its handle — flip it (reuses the tested path), whether or not the
  // handle arrived on this poll's first response: `recovered` says only that, and the poll restarts
  // whenever the panel remounts, so it cannot stand for "dispatched before this page". A draft that
  // adopted no handle would leave the user's own dispatch on a second, duplicate row.
  if (0 <= items.findIndex(item => isSkipperGuidedDraftReport(item))) {
    return markSkipperGuidedDraftGenerating(props)
  }
  // Already tracking a generating placeholder — nothing to add.
  if (0 <= items.findIndex(item => 'skipper' == item.get('type') && 'generating' == item.get('status'))) return

  const newListItem = buildGuidedPlaceholderItem('generating', { generation }, reportId)
  const staticReports = items.filter(
    (r, index) => 0 != index && STATIC_REPORTS_IDS.includes(r.get('type')) && !isSkipperGuidedReport(r)
  )
  const virtualReports = items.filter(r => VIRTUAL_REPORTS_IDS.includes(r.get('type')))
  const position = staticReports.size + virtualReports.size + 1

  const newListModel = listModel.update('items', List(), list => list.insert(position, newListItem))

  // select: false — a generation recovered on mount must not relocate the user (Key decision #15).
  // The add action selects the row it adds, which is what the Create button depends on.
  actions.updateModel(newListModel, {
    type: 'add',
    id: newListItem.get('id'),
    status: 'generating',
    select: false
  })
}

// Swap the placeholder skeleton for the report the generation poll carries: id, title, body,
// share link — the same object the page bootstrap serves. `status` is stripped because it is the
// client-side lifecycle flag here ('draft' / 'generating' / 'ready'), owned by the caller, while
// the server publishes its own in-flight generation status under the same key. The local
// guided_report keeps its launch handle (the poll key and the reload guard read it) and takes the
// server's snapshot on top.
const mergeGuidedReportStructure = (row, report) => {
  if (!report) return row

  const incoming = fromJS(report).delete('status')
  const id = incoming.get('id')
  const structure = Number.isInteger(id) && 0 < id ? incoming : incoming.delete('id')
  const guided = (row.get('guided_report') || Map()).merge(structure.get('guided_report') || Map())

  return row.merge(structure).set('guided_report', guided)
}

// Set the status of the in-session generating guided draft, targeting the row for
// THIS generation's report by id (the row adopts the real report id once the launch
// handle carries it). Falls back to the sole in-flight draft when the id is unknown
// or still temporary, but never guesses among several. Merges the persisted report
// when one is given. Returns whether a row was updated. No-ops when the draft is
// gone (reloaded, deleted, or switched away).
const setGuidedGeneratingDraftStatus = (listModel, actions, status, reportId = null, report = null) => {
  const items = listModel.get('items')
  const generating = item => 'skipper' == item.get('type') && 'generating' == item.get('status')
  // Only a guided row may be *guessed* at. The auto-launched report is served 'generating' for its
  // own regeneration, so without provenance the guess can demote a report that is never deletable,
  // and it counts toward the uniqueness test that decides whether to guess at all. An explicit
  // reportId names its row and needs no such test — a regeneration dispatched from the chat may
  // legitimately target the auto-launched report.
  const guessable = item => generating(item) && isSkipperGuidedReport(item)

  let index = null != reportId ? items.findIndex(item => generating(item) && reportId == item.get('id')) : -1
  if (0 > index && 1 == items.count(guessable)) index = items.findIndex(guessable)
  if (0 > index) return false

  const draft = listModel.getIn(['items', index])
  const prevId = draft.get('id')
  const updatedItem = mergeGuidedReportStructure(draft, report).set('status', status)
  const newListModel = listModel.setIn(['items', index], updatedItem)
  const id = updatedItem.get('id')

  actions.updateModel(newListModel, id != prevId ? { type: 'reid', id, prevId } : { type: 'update', id })
  return true
}

// Adopt the persisted report while the generation is still running: the stat server saves the row
// before enqueueing the job, so the poll hands it over mid-flight. The row stops being client-only
// (real id, server body) and stays 'generating' until the terminal tick.
export const adoptSkipperGuidedDraftReport = (props = {}) => {
  const { listModel, actions, reportId = null, report = null } = props
  return setGuidedGeneratingDraftStatus(listModel, actions, 'generating', reportId, report)
}

// Flip the generating guided draft to a "ready" state once the polling_url job poll reports
// completion, merging the finished report the same poll carries.
export const markSkipperGuidedDraftReady = (props = {}) => {
  const { listModel, actions, reportId = null, report = null } = props
  return setGuidedGeneratingDraftStatus(listModel, actions, 'ready', reportId, report)
}

export const updateListItem = (props = {}) => {
  const { url, listState, listModel, model, actions } = props
  const id = model.get('id')

  updateListState({ updates: [{ path: reportStatePath(id, 'process'), value: 'save' }], listState, actions })

  const data = getModelData(model)
  data.edited = model.get('edited') || model.get('created')

  wrappedPostFetch(url, {
    do: 'update',
    id: model.get('id'),
    share: data
  })
    .then(data => {
      if (!Number.isNaN(Number(data.id)) && 0 < Number(data.id)) {
        const modelPath = reportModelPath(listModel, id)
        const newListModel = listModel.withMutations(map => {
          map.setIn([...modelPath, 'edited'], data.edited).setIn([...modelPath, 'editor'], data.editor)
        })
        actions.updateModel(newListModel, { type: 'update', id })
      } else {
        actions.error(data.error || ERROR, id, {
          report: data.report
        })
      }
    })
    .catch(() => actions.error(ERROR, id))
}

export const deleteListItem = (props = {}) => {
  const { url, listState, listModel, model, actions } = props
  const id = model.get('id')
  const modelPath = reportModelPath(listModel, id)

  // A Skipper guided draft is a client-only placeholder (negative id, never
  // persisted) — no backend row to delete, so drop it from the list locally
  // instead of round-tripping.
  if (isSkipperGuidedDraftReport(model)) {
    const withoutItem = listModel.deleteIn([...modelPath])
    const newListModel = encodeModelData(withoutItem, decodeModelData(withoutItem))
    actions.updateModel(newListModel, { type: 'delete', id })
    return
  }

  const newListState = updateListState({
    updates: [{ path: reportStatePath(id, 'process'), value: 'delete' }],
    listState,
    actions
  })

  wrappedPostFetch(url, {
    do: 'delete',
    id
  })
    .then(data => {
      if (data.status) {
        updateListState({
          updates: [
            { path: reportStatePath(id, 'deleted'), value: true },
            { path: reportStatePath(id, 'active'), value: false }
          ],
          listState: newListState,
          actions
        })

        setTimeout(() => {
          const newListModel =
            0 == listModel.get('items').findIndex(item => item.get('id') == id)
              ? listModel.withMutations(map => {
                  const itemIndex = map
                    .get('items')
                    .findIndex(
                      (r, index) =>
                        0 < index &&
                        !r.get('hidden') &&
                        ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.get('type'))
                    )
                  const listItem = fromJS(map.getIn(['items', itemIndex]))
                  map
                    .deleteIn(['items', itemIndex])
                    .deleteIn([...modelPath])
                    .update('items', List(), items => items.insert(0, listItem))
                })
              : encodeModelData(listModel.deleteIn([...modelPath]), decodeModelData(listModel.deleteIn([...modelPath])))
          actions.updateModel(newListModel, { type: 'delete', id })
        }, 500)
      } else {
        if (data.report) {
          const listItem = fromJS(data.report)
          const newListModel = listModel.withMutations(map => {
            const itemIndex = map.get('items').findIndex(item => item.get('id') == listItem.get('id'))
            map.deleteIn(['items', itemIndex]).update('items', List(), items => items.insert(itemIndex, listItem))
          })
          actions.updateModel(newListModel, { type: 'update', id: listItem.get('id') })
        }

        actions.error(data.error || ERROR, id)
      }
    })
    .catch(() => actions.error(ERROR, id))
}

// `updates` paths are relative to the report's own model entry.
export const updateItemModel = (props = {}) => {
  const { url, data, updates, listState, listModel, model, actions } = props
  const id = model.get('id')

  updateListState({ updates: [{ path: reportStatePath(id, 'process'), value: 'update' }], listState, actions })

  data.edited = model.get('edited') || model.get('created')

  wrappedPostFetch(url, data)
    .then(data => {
      if (data.status) {
        const modelPath = reportModelPath(listModel, id)
        const newListModel = listModel.withMutations(map => {
          updates.forEach(x => {
            map.setIn([...modelPath, ...x.path], x.value)
          })
          map.setIn([...modelPath, 'edited'], data.edited).setIn([...modelPath, 'editor'], data.editor)
        })
        actions.updateModel(newListModel, { type: 'update_props', id })
      } else {
        actions.error(data.error || ERROR, id, {
          report: data.report
        })
      }
    })
    .catch(() => actions.error(ERROR, id))
}

export const updateItemShareLink = (props = {}) => {
  const { url, share, listState, listModel, model, actions } = props
  const id = model.get('id')

  const process = share ? 'share' : 'unshare'

  updateListState({ updates: [{ path: reportStatePath(id, 'process'), value: process }], listState, actions })

  wrappedPostFetch(url, { do: process, id, edited: model.get('edited') || model.get('created') })
    .then(data => {
      if (data.status) {
        const modelPath = reportModelPath(listModel, id)
        const newListModel = listModel.withMutations(map => {
          map
            .setIn([...modelPath, 'shareLink'], data.shareLink)
            .setIn([...modelPath, 'edited'], data.edited)
            .setIn([...modelPath, 'editor'], data.editor)
        })
        actions.updateModel(newListModel, { type: 'update_props', id })
      } else {
        actions.error(data.error || ERROR, id, {
          report: data.report
        })
      }
    })
    .catch(() => actions.error(ERROR, id))
}

export const revertListItem = (props = {}) => {
  const { listState, listModel, state, model, actions } = props
  const id = model.get('id')

  updateListState({ updates: [{ path: reportStatePath(id, 'process'), value: 'revert' }], listState, actions })

  const newListModel = setModelData(state, listModel, reportModelPath(listModel, id))
  actions.updateModel(newListModel, { type: 'revert', id })
}

export const RenameAllCodebooks = (url, codebook, tags, reports, callback) => {
  wrappedPostFetch(url, {
    do: 'rename_codebook',
    from: codebook.from,
    to: codebook.to,
    tags,
    reports
  })
    .then(data => callback(data))
    .catch(error => callback({ status: false, error }))
}

export const RenameAllTags = (url, tag, reports, callback) => {
  wrappedPostFetch(url, {
    do: 'rename_tag',
    from: tag.from,
    to: tag.to,
    reports
  })
    .then(data => callback(data))
    .catch(error => callback({ status: false, error }))
}
